import {
  Bookmark,
  BookmarkCheck,
  ChevronRight,
  Clock,
  Heart,
  MapPin,
  ThumbsUp,
} from 'lucide-react';
import { Link } from 'wouter';
import type { CalendarEntryDto } from '@shared/api-contracts';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { formatSessionTime, formatTimeInTimezone } from '@/lib/geography';
import { cn } from '@/lib/utils';

export type CalendarEntry = CalendarEntryDto;

export const CATEGORY_LABELS: Record<string, string> = {
  class_session: 'Classes',
  farmers_market: 'Farmers Markets',
  community: 'Community',
  library: 'Library',
  workshop: 'Workshops',
  arts: 'Arts',
  sports: 'Sports',
  food_drink: 'Food & Drink',
  outdoor: 'Outdoor',
  wellness: 'Wellness',
  music: 'Music',
  government: 'Government',
  other: 'Other',
};

export const CATEGORY_DOT: Record<string, string> = {
  class_session: 'bg-primary',
  farmers_market: 'bg-green-500',
  community: 'bg-purple-500',
  library: 'bg-blue-500',
  workshop: 'bg-orange-500',
  arts: 'bg-pink-500',
  sports: 'bg-red-500',
  food_drink: 'bg-amber-500',
  outdoor: 'bg-teal-500',
  wellness: 'bg-rose-400',
  music: 'bg-violet-500',
  government: 'bg-slate-500',
  other: 'bg-muted-foreground',
};

export const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function formatGroupHeader(dateStr: string, tz: string): string {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: tz });
  const tomorrow = new Date(Date.now() + 86_400_000).toLocaleDateString('en-CA', { timeZone: tz });
  if (dateStr === today) return 'Today';
  if (dateStr === tomorrow) return 'Tomorrow';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export function getEntryDayKey(entry: CalendarEntry): string {
  return new Date(entry.startsAt).toLocaleDateString('en-CA', { timeZone: entry.timezone });
}

export function shortTzLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'long' })
      .formatToParts(new Date())
      .find(p => p.type === 'timeZoneName');
    return parts?.value ?? tz;
  } catch {
    return tz;
  }
}

/**
 * The canonical calendar entry card — shared by the region/locality calendar
 * and any venue-scoped agenda (e.g. the location detail page) so both
 * present entries identically. Save/RSVP affordances only render when their
 * handlers are supplied, so a read-only caller can omit them.
 */
export function EntryCard({
  entry,
  basePath: _basePath,
  onOpen,
  isSaved,
  onSave,
  rsvpStatus,
  onRsvp,
  onCancelRsvp,
  goingCount = 0,
  interestedCount = 0,
}: {
  entry: CalendarEntry;
  basePath: string;
  onOpen?: () => void;
  isSaved?: boolean;
  onSave?: () => void;
  rsvpStatus?: 'going' | 'interested' | null;
  onRsvp?: (status: 'going' | 'interested') => void;
  onCancelRsvp?: () => void;
  goingCount?: number;
  interestedCount?: number;
}) {
  const isEvent = entry.kind === 'event';
  const categoryLabel =
    entry.kind === 'session'
      ? 'Class'
      : (entry.category && CATEGORY_LABELS[entry.category]) || 'Event';
  const dotClass = CATEGORY_DOT[entry.category ?? 'other'] ?? 'bg-muted-foreground';
  const locationLabel = entry.venueName ?? entry.sourceName ?? entry.creatorName;
  const meta = [locationLabel].filter(Boolean).join(' · ');

  return (
    <Card
      className={cn(
        'rounded-[1.75rem] border-border/70 shadow-sm transition-shadow',
        onOpen && 'cursor-pointer hover:shadow-md'
      )}
      onClick={onOpen}
    >
      {/* ── Mobile compact row ─────────────────────────────────────── */}
      <div className="sm:hidden flex items-center gap-3 px-4 py-3.5">
        {/* Category dot */}
        <div className="shrink-0 flex flex-col items-center gap-1.5">
          <div className={cn('h-2.5 w-2.5 rounded-full', dotClass)} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[0.9rem] leading-snug line-clamp-1">{entry.title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
            {formatTimeInTimezone(entry.startsAt, entry.timezone)}
            {' · '}
            {categoryLabel}
            {meta ? ` · ${meta}` : ''}
            {entry.attendanceMode && entry.attendanceMode !== 'in_person'
              ? ` · ${entry.attendanceMode === 'hybrid' ? 'Hybrid' : 'Online'}`
              : ''}
          </p>
        </div>

        {/* Right actions */}
        <div className="shrink-0 flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
          {isEvent && onSave ? (
            <button
              type="button"
              aria-label={isSaved ? 'Unsave event' : 'Save event'}
              onClick={e => {
                e.stopPropagation();
                onSave();
              }}
              className="rounded-full p-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              {isSaved ? (
                <BookmarkCheck className="h-4 w-4 text-primary" />
              ) : (
                <Bookmark className="h-4 w-4" />
              )}
            </button>
          ) : null}
          {isEvent && onRsvp ? (
            <>
              <button
                type="button"
                onClick={() => (rsvpStatus === 'going' ? onCancelRsvp?.() : onRsvp('going'))}
                className={cn(
                  'rounded-full border p-1.5 transition-colors',
                  rsvpStatus === 'going'
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border/70 text-muted-foreground'
                )}
                aria-label="Going"
              >
                <ThumbsUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() =>
                  rsvpStatus === 'interested' ? onCancelRsvp?.() : onRsvp('interested')
                }
                className={cn(
                  'rounded-full border p-1.5 transition-colors',
                  rsvpStatus === 'interested'
                    ? 'border-rose-500 bg-rose-500 text-white'
                    : 'border-border/70 text-muted-foreground'
                )}
                aria-label="Interested"
              >
                <Heart className="h-3.5 w-3.5" />
              </button>
            </>
          ) : onOpen ? (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          ) : null}
        </div>
      </div>

      {/* ── Desktop full card ───────────────────────────────────────── */}
      <CardContent className="hidden sm:block space-y-4 p-6">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={entry.kind === 'session' ? 'default' : 'secondary'}
              className="rounded-full px-3 py-1 gap-1.5"
            >
              <span className={cn('h-2 w-2 rounded-full', dotClass)} />
              {categoryLabel}
            </Badge>
            {entry.attendanceMode && entry.attendanceMode !== 'in_person' ? (
              <Badge variant="outline" className="rounded-full px-3 py-1">
                {entry.attendanceMode === 'hybrid' ? 'Hybrid' : 'Online'}
              </Badge>
            ) : null}
          </div>
          {isEvent && onSave ? (
            <button
              type="button"
              aria-label={isSaved ? 'Unsave event' : 'Save event'}
              onClick={e => {
                e.stopPropagation();
                onSave();
              }}
              className="rounded-full p-1.5 text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              {isSaved ? (
                <BookmarkCheck className="h-5 w-5 text-primary" />
              ) : (
                <Bookmark className="h-5 w-5" />
              )}
            </button>
          ) : null}
        </div>

        <div>
          <h3 className="text-xl font-semibold">{entry.title}</h3>
          {entry.description && (
            <p className="mt-1 text-sm leading-6 text-muted-foreground line-clamp-2">
              {entry.description}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
          <span>
            {formatSessionTime(entry.startsAt, entry.endsAt ?? entry.startsAt, entry.timezone)}
          </span>
          {locationLabel ? (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {locationLabel}
            </span>
          ) : null}
          {entry.timezone && entry.timezone !== BROWSER_TZ ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/60 px-2 py-0.5 text-xs">
              <Clock className="h-3 w-3" />
              {shortTzLabel(entry.timezone)}
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {entry.kind === 'session' && entry.classId ? (
            <Link href={`/classes/${entry.classId}`} onClick={e => e.stopPropagation()}>
              <Button className="rounded-full px-5">View class</Button>
            </Link>
          ) : null}
          {entry.kind === 'event' && entry.externalUrl ? (
            <a
              href={entry.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              <Button variant="outline" className="rounded-full px-5">
                Learn more
              </Button>
            </a>
          ) : null}
          {onOpen ? (
            <Button
              variant="ghost"
              className="rounded-full px-5 gap-1"
              onClick={e => {
                e.stopPropagation();
                onOpen();
              }}
            >
              Details <ChevronRight className="h-4 w-4" />
            </Button>
          ) : null}
          {isEvent && onRsvp ? (
            <div className="ml-auto flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => (rsvpStatus === 'going' ? onCancelRsvp?.() : onRsvp('going'))}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                  rsvpStatus === 'going'
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border/70 text-muted-foreground hover:border-primary hover:text-primary'
                )}
                aria-label="Going"
              >
                <ThumbsUp className="h-3 w-3" />
                {goingCount > 0 ? goingCount : 'Going'}
              </button>
              <button
                type="button"
                onClick={() =>
                  rsvpStatus === 'interested' ? onCancelRsvp?.() : onRsvp('interested')
                }
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                  rsvpStatus === 'interested'
                    ? 'border-rose-500 bg-rose-500 text-white'
                    : 'border-border/70 text-muted-foreground hover:border-rose-400 hover:text-rose-500'
                )}
                aria-label="Interested"
              >
                <Heart className="h-3 w-3" />
                {interestedCount > 0 ? interestedCount : 'Interested'}
              </button>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
